/**
 * Codificação/decodificação WAV (PCM 16 e 24 bit) sem dependências.
 * Funções puras: testáveis em Node.
 */

export interface WavData {
  sampleRate: number;
  channels: number;
  bitDepth: 16 | 24;
  /** Um Float32Array por canal, todos com o mesmo comprimento. */
  data: Float32Array[];
}

function writeString(view: DataView, offset: number, str: string) {
  for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i));
}

export function encodeWav(
  data: Float32Array[],
  sampleRate: number,
  bitDepth: 16 | 24 = 16,
  options: { dither?: boolean; clipProtect?: boolean } = {}
): ArrayBuffer {
  const channels = Math.max(1, data.length);
  const length = data.reduce((m, d) => Math.max(m, d.length), 0);
  const bytesPerSample = bitDepth / 8;
  const blockAlign = channels * bytesPerSample;
  const dataSize = length * blockAlign;
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);

  writeString(view, 0, 'RIFF');
  view.setUint32(4, 36 + dataSize, true);
  writeString(view, 8, 'WAVE');
  writeString(view, 12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, channels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * blockAlign, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, bitDepth, true);
  writeString(view, 36, 'data');
  view.setUint32(40, dataSize, true);

  const max = bitDepth === 16 ? 32767 : 8388607;
  let offset = 44;
  const dither = options.dither ?? bitDepth === 16;
  for (let i = 0; i < length; i++) {
    for (let ch = 0; ch < channels; ch++) {
      let s = i < data[ch].length ? data[ch][i] : 0;
      if (!isFinite(s)) s = 0;
      if (options.clipProtect !== false) s = Math.max(-1, Math.min(1, s));
      if (dither) s += (Math.random() * 2 - 1) / (max * 2);
      const v = Math.round(s * max);
      if (bitDepth === 16) {
        view.setInt16(offset, Math.max(-32768, Math.min(32767, v)), true);
        offset += 2;
      } else {
        const clamped = Math.max(-8388608, Math.min(8388607, v));
        view.setUint8(offset, clamped & 0xff);
        view.setUint8(offset + 1, (clamped >> 8) & 0xff);
        view.setUint8(offset + 2, (clamped >> 16) & 0xff);
        offset += 3;
      }
    }
  }
  return buffer;
}

export function decodeWav(arrayBuffer: ArrayBuffer): WavData {
  const view = new DataView(arrayBuffer);
  if (arrayBuffer.byteLength < 44) throw new Error('WAV inválido: arquivo muito curto');
  const riff = String.fromCharCode(view.getUint8(0), view.getUint8(1), view.getUint8(2), view.getUint8(3));
  const wave = String.fromCharCode(view.getUint8(8), view.getUint8(9), view.getUint8(10), view.getUint8(11));
  if (riff !== 'RIFF' || wave !== 'WAVE') throw new Error('Arquivo não é WAV (RIFF)');

  let offset = 12;
  let fmt: { channels: number; sampleRate: number; bitDepth: number; format: number } | null = null;
  let dataOffset = -1;
  let dataSize = 0;

  while (offset + 8 <= view.byteLength) {
    const id = String.fromCharCode(
      view.getUint8(offset), view.getUint8(offset + 1), view.getUint8(offset + 2), view.getUint8(offset + 3)
    );
    const size = view.getUint32(offset + 4, true);
    const body = offset + 8;
    if (id === 'fmt ') {
      fmt = {
        format: view.getUint16(body, true),
        channels: view.getUint16(body + 2, true),
        sampleRate: view.getUint32(body + 4, true),
        bitDepth: view.getUint16(body + 14, true),
      };
    } else if (id === 'data') {
      dataOffset = body;
      dataSize = Math.min(size, view.byteLength - body);
      break;
    }
    offset = body + size + (size % 2);
  }
  if (!fmt || dataOffset < 0) throw new Error('WAV inválido: chunks fmt/data ausentes');
  if (fmt.format !== 1) throw new Error('WAV não-PCM (compressão) não é suportado aqui');
  if (fmt.bitDepth !== 16 && fmt.bitDepth !== 24 && fmt.bitDepth !== 8 && fmt.bitDepth !== 32)
    throw new Error(`Profundidade não suportada: ${fmt.bitDepth} bits`);

  const bytesPerSample = fmt.bitDepth / 8;
  const frameCount = Math.floor(dataSize / (bytesPerSample * fmt.channels));
  const out: Float32Array[] = [];
  for (let c = 0; c < fmt.channels; c++) out.push(new Float32Array(frameCount));

  for (let i = 0; i < frameCount; i++) {
    for (let c = 0; c < fmt.channels; c++) {
      const p = dataOffset + (i * fmt.channels + c) * bytesPerSample;
      let v = 0;
      if (fmt.bitDepth === 16) v = view.getInt16(p, true) / 32768;
      else if (fmt.bitDepth === 24) {
        let x = view.getUint8(p) | (view.getUint8(p + 1) << 8) | (view.getUint8(p + 2) << 16);
        if (x & 0x800000) x |= ~0xffffff;
        v = x / 8388608;
      } else if (fmt.bitDepth === 8) v = (view.getUint8(p) - 128) / 128;
      else v = view.getFloat32(p, true);
      out[c][i] = v;
    }
  }
  return {
    sampleRate: fmt.sampleRate,
    channels: fmt.channels,
    bitDepth: (fmt.bitDepth === 24 ? 24 : 16) as 16 | 24,
    data: out,
  };
}

/** Converte AudioBuffer -> WAV ArrayBuffer. */
export function audioBufferToWav(
  buffer: AudioBuffer,
  bitDepth: 16 | 24 = 16,
  options: { dither?: boolean } = {}
): ArrayBuffer {
  const chans: Float32Array[] = [];
  for (let c = 0; c < buffer.numberOfChannels; c++) chans.push(buffer.getChannelData(c));
  return encodeWav(chans, buffer.sampleRate, bitDepth, options);
}

/** Cria um AudioBuffer a partir de WAV (útil em testes e no worker de export). */
export function wavToChannelData(arrayBuffer: ArrayBuffer): WavData {
  return decodeWav(arrayBuffer);
}
